/* global __APP_VERSION__ */
import axios from "axios";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { ClientToken } from "utils/Constant";
import { getCookieValue } from "utils/Storage";

// Deliberately a bare axios call, NOT the shared `httpClient` — that
// instance's response interceptor force-reloads the whole page to /login
// on any 403 (see utils/Api.js), which would nuke this panel a second
// after opening it whenever there's no valid session. This ping only
// needs to know whether the API is reachable, not whether we're logged in.
const pingClient = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
});

const BREAKPOINTS = [
  ["sm", 640],
  ["md", 768],
  ["lg", 1024],
  ["xl", 1280],
  ["2xl", 1536],
];

const currentBreakpoint = (w) => {
  let current = "base (<640)";
  for (const [name, min] of BREAKPOINTS) {
    if (w >= min) current = `${name} (>=${min})`;
  }
  return current;
};

// A crash whose stack traces into a browser extension/userscript (not our
// own bundle) isn't an app bug — it's third-party code injected into the
// page throwing on its own. Flag it clearly so it doesn't get chased as
// eN-LIS bug next time; the fix there is removing/disabling that extension
// on the device, not changing our code.
const EXTENSION_SCHEME_RE = /(chrome|moz|safari|edge)-extension:\/\//i;
const isLikelyExtensionError = (err) =>
  Boolean(
    err &&
      (EXTENSION_SCHEME_RE.test(err.stack || "") ||
        EXTENSION_SCHEME_RE.test(err.source || "") ||
        EXTENSION_SCHEME_RE.test(err.componentStack || "")),
  );

const Row = ({ label, value }) => (
  <div
    style={{
      display: "flex",
      gap: 16,
      padding: "6px 0",
      borderBottom: "1px solid #1a2e1a",
    }}
  >
    <div style={{ width: 280, flexShrink: 0, color: "#6fae6f" }}>{label}</div>
    <div style={{ color: "#0f0", wordBreak: "break-word" }}>{value}</div>
  </div>
);

Row.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

const SectionTitle = ({ children }) => (
  <div
    style={{
      marginTop: 20,
      marginBottom: 4,
      color: "#fff",
      fontWeight: "bold",
      fontSize: 15,
      textTransform: "uppercase",
      letterSpacing: 1,
    }}
  >
    {children}
  </div>
);

SectionTitle.propTypes = {
  children: PropTypes.node.isRequired,
};

// Wipes everything the app could be caching client-side, then hard-reloads.
// Best-effort — a TV browser may not support all of these APIs, so each
// step is isolated so one failure doesn't stop the rest.
const clearAllCaches = async () => {
  try {
    localStorage.clear();
  } catch (e) {
    /* unavailable */
  }
  try {
    sessionStorage.clear();
  } catch (e) {
    /* unavailable */
  }
  try {
    document.cookie.split(";").forEach((c) => {
      const name = c.split("=")[0].trim();
      if (name) document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    });
  } catch (e) {
    /* unavailable */
  }
  try {
    if (window.caches?.keys) {
      const keys = await window.caches.keys();
      await Promise.all(keys.map((k) => window.caches.delete(k)));
    }
  } catch (e) {
    /* unavailable */
  }
  try {
    if (navigator.serviceWorker?.getRegistrations) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }
  } catch (e) {
    /* unavailable */
  }
  window.location.reload();
};

const TvDebugButton = ({ crashError = null }) => {
  const [open, setOpen] = useState(false);
  const [netStatus, setNetStatus] = useState("not checked");
  const [errorClearTick, setErrorClearTick] = useState(0);
  const [confirmClear, setConfirmClear] = useState(false);

  const currentRoute =
    typeof window !== "undefined" && window.location
      ? `${window.location.pathname}${window.location.search}`
      : "n/a";

  // Tells the index.html boot-time script the React app made it up, so it
  // never renders its own fallback gear/panel on top of this one — one
  // diagnostics UI per TV, not two.
  useEffect(() => {
    window.__ENLIS_APP_BOOTED__ = true;
  }, []);

  const baseDiag = typeof window !== "undefined" ? window.__ENLIS_DIAG__ : null;
  const width = typeof window !== "undefined" ? window.innerWidth : 0;
  const height = typeof window !== "undefined" ? window.innerHeight : 0;

  // eslint-disable-next-line no-unused-vars -- read to force recompute after "clear last error"
  const _errorClearTick = errorClearTick;
  let lastError = null;
  try {
    const raw = localStorage.getItem("enlis_last_error");
    lastError = raw ? JSON.parse(raw) : null;
  } catch (e) {
    lastError = null;
  }

  if (!lastError && crashError) {
    lastError = {
      message: crashError?.toString?.() || String(crashError),
      stack: crashError?.stack || null,
      route: currentRoute,
      time: new Date().toISOString(),
      source: "React ErrorBoundary",
    };
  }

  useEffect(() => {
    if (!open) setConfirmClear(false);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setNetStatus("checking…");
    const start = performance.now();
    const token = getCookieValue(ClientToken);
    pingClient
      .get("/users/me/", {
        timeout: 8000,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
      .then(() => {
        setNetStatus(`OK (${Math.round(performance.now() - start)}ms)`);
      })
      .catch((err) => {
        const status = err?.response?.status;
        if (status) {
          setNetStatus(
            `reached server, HTTP ${status} (${Math.round(performance.now() - start)}ms)`,
          );
        } else {
          setNetStatus(`unreachable: ${err?.message || "unknown error"}`);
        }
      });
  }, [open]);

  const hasToken = Boolean(getCookieValue(ClientToken));

  return (
    <>
      {open && (
        <div
          tabIndex={0}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 2147483647,
            background: "#050805",
            color: "#0f0",
            fontFamily: "monospace",
            fontSize: 16,
            padding: "24px",
            overflowY: "auto",
            overscrollBehavior: "contain",
          }}
        >
          <div
            style={{
              fontSize: 20,
              fontWeight: "bold",
              color: "#fff",
              marginBottom: 8,
            }}
          >
            eN-LIS TV DIAGNOSTICS (live)
          </div>

          <button
            type="button"
            onClick={() => {
              if (confirmClear) {
                clearAllCaches();
              } else {
                setConfirmClear(true);
              }
            }}
            style={{
              background: confirmClear ? "#4a0000" : "#111",
              color: confirmClear ? "#ff5555" : "#0f0",
              border: `1px solid ${confirmClear ? "#ff5555" : "#0f0"}`,
              borderRadius: 4,
              padding: "6px 12px",
              fontFamily: "monospace",
              fontSize: 14,
              cursor: "pointer",
              marginBottom: 8,
            }}
          >
            {confirmClear
              ? "Tap again to confirm — clears cache & logs out"
              : "Clear cache"}
          </button>

          <SectionTitle>
            Browser capabilities (from boot-time script)
          </SectionTitle>
          {baseDiag ? (
            <>
              <Row
                label="ES module <script> support"
                value={String(baseDiag.moduleSupported)}
              />
              <Row
                label="optional-chaining / ?? support"
                value={String(baseDiag.opChainSupported)}
              />
              <Row
                label="fetch support"
                value={String(baseDiag.fetchSupported)}
              />
              <Row
                label="Promise support"
                value={String(baseDiag.promiseSupported)}
              />
              <Row
                label="localStorage / sessionStorage / cookie"
                value={`${baseDiag.storageOk?.local} / ${baseDiag.storageOk?.session} / ${baseDiag.storageOk?.cookie}`}
              />
            </>
          ) : (
            <Row
              label="status"
              value="not available (window.__ENLIS_DIAG__ missing)"
            />
          )}

          {lastError && (
            <>
              <SectionTitle>Last captured error</SectionTitle>
              {isLikelyExtensionError(lastError) && (
                <div
                  style={{
                    background: "#3a1a00",
                    border: "1px solid #ffb300",
                    color: "#ffb300",
                    borderRadius: 4,
                    padding: "8px 10px",
                    marginBottom: 8,
                    fontSize: 14,
                  }}
                >
                  ⚠ This error&apos;s stack points into a browser extension
                  (chrome-extension://…), not the eN-LIS app itself. This is
                  most likely a third-party extension installed on this
                  TV&apos;s browser conflicting with the page —
                  check/disable extensions on this device rather than
                  treating it as an app bug.
                </div>
              )}
              <Row label="time" value={lastError.time || "n/a"} />
              <Row label="route" value={lastError.route || "n/a"} />
              <Row
                label="source"
                value={lastError.source || "React ErrorBoundary"}
              />
              <Row label="message" value={lastError.message || "n/a"} />
              {lastError.errorType && (
                <Row
                  label="typeof error / instanceof Error"
                  value={`${lastError.errorType} / ${lastError.isErrorInstance}`}
                />
              )}
              {lastError.componentStack && (
                <div
                  style={{
                    padding: "6px 0",
                    borderBottom: "1px solid #1a2e1a",
                  }}
                >
                  <div style={{ color: "#6fae6f", marginBottom: 4 }}>
                    component stack
                  </div>
                  <pre
                    style={{
                      color: "#0f0",
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                      margin: 0,
                      fontSize: 13,
                    }}
                  >
                    {lastError.componentStack}
                  </pre>
                </div>
              )}
              {lastError.stack && (
                <div
                  style={{
                    padding: "6px 0",
                    borderBottom: "1px solid #1a2e1a",
                  }}
                >
                  <div style={{ color: "#6fae6f", marginBottom: 4 }}>stack</div>
                  <pre
                    style={{
                      color: "#0f0",
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                      margin: 0,
                      fontSize: 13,
                    }}
                  >
                    {lastError.stack}
                  </pre>
                </div>
              )}
              <button
                type="button"
                onClick={() => {
                  localStorage.removeItem("enlis_last_error");
                  setErrorClearTick((t) => t + 1);
                }}
                style={{
                  marginTop: 8,
                  background: "#111",
                  color: "#0f0",
                  border: "1px solid #0f0",
                  borderRadius: 4,
                  padding: "4px 10px",
                  fontFamily: "monospace",
                  fontSize: 13,
                  cursor: "pointer",
                }}
              >
                clear last error
              </button>
            </>
          )}

          <SectionTitle>App / route</SectionTitle>
          <Row
            label="app version"
            value={
              typeof __APP_VERSION__ !== "undefined" ? __APP_VERSION__ : "n/a"
            }
          />
          <Row label="route" value={currentRoute} />
          <Row
            label="boot-time reading collected at"
            value={baseDiag?.time || "n/a"}
          />
          <Row
            label="user agent"
            value={baseDiag?.userAgent || navigator.userAgent}
          />

          <SectionTitle>Display</SectionTitle>
          <Row
            label="innerWidth x innerHeight"
            value={`${width} x ${height}`}
          />
          <Row
            label="screen.width x height"
            value={
              baseDiag
                ? `${baseDiag.screenWidth} x ${baseDiag.screenHeight}`
                : "n/a"
            }
          />
          <Row
            label="screen.availWidth x availHeight"
            value={
              baseDiag
                ? `${baseDiag.screenAvailWidth} x ${baseDiag.screenAvailHeight}`
                : "n/a"
            }
          />
          <Row
            label="tailwind breakpoint bucket"
            value={currentBreakpoint(width)}
          />
          <Row label="devicePixelRatio" value={window.devicePixelRatio} />

          <SectionTitle>Auth / network</SectionTitle>
          <Row label="auth token cookie present" value={String(hasToken)} />
          <Row label="API base URL" value={import.meta.env.VITE_API_URL} />
          <Row label="API reachability (GET /users/me/)" value={netStatus} />

          <SectionTitle>Hardware / platform</SectionTitle>
          {baseDiag ? (
            <>
              <Row label="platform" value={baseDiag.platform} />
              <Row label="vendor" value={baseDiag.vendor} />
              <Row
                label="CPU logical cores (hardwareConcurrency)"
                value={baseDiag.hardwareConcurrency}
              />
              <Row
                label="deviceMemory (GB, Chrome-only)"
                value={baseDiag.deviceMemory}
              />
              <Row label="maxTouchPoints" value={baseDiag.maxTouchPoints} />
              <Row
                label="language / languages"
                value={`${baseDiag.language} / ${baseDiag.languages}`}
              />
              <Row label="timezone" value={baseDiag.timeZone} />
              <Row label="network connection" value={baseDiag.connectionInfo} />
              <Row label="GPU vendor (WebGL)" value={baseDiag.webglVendor} />
              <Row
                label="GPU renderer (WebGL)"
                value={baseDiag.webglRenderer}
              />
            </>
          ) : (
            <Row
              label="status"
              value="not available (window.__ENLIS_DIAG__ missing)"
            />
          )}
        </div>
      )}

      <button
        type="button"
        aria-label={open ? "Close TV diagnostics" : "Open TV diagnostics"}
        onClick={() => setOpen((o) => !o)}
        style={{
          position: "fixed",
          right: 16,
          top: 16,
          zIndex: 2147483647,
          width: 40,
          height: 40,
          borderRadius: "50%",
          background: "#111",
          color: "#0f0",
          border: "2px solid #0f0",
          fontSize: 20,
          lineHeight: 1,
          cursor: "pointer",
        }}
      >
        {open ? "✕" : "⚙"}
      </button>
    </>
  );
};

TvDebugButton.propTypes = {
  crashError: PropTypes.any,
};

export default TvDebugButton;
